<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onDestroy, onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { PropertyCard, Text } from '@Shelly/shelly-design-system';
	import { getTimeFormat } from 'src/helpers/date_helper';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		fetchDeviceSchedules,
		getCachedDeviceSchedules,
		getDeviceScheduleRev,
		type DeviceSchedulesFetchResult,
	} from 'src/helpers/next_schedule_device_helpers';
	import {
		findNextSchedule,
		formatNextScheduleAction,
		formatNextScheduleTarget,
		formatNextScheduleValue,
		nextScheduleActionIcon,
		parseNextScheduleActions,
		type NextScheduleAction,
		type NextScheduleOccurrence,
	} from 'src/helpers/next_schedule_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	export let device: Device;
	export let stale = false;
	export let disabled = false;

	$: settingsStore = getDeviceSettings(real_id(device.id));
	$: settings = $settingsStore;

	let occurrence: NextScheduleOccurrence | undefined;
	let actions: NextScheduleAction[] = [];
	let loadSeq = 0;
	let scheduleRev: number | undefined;
	let watchingRev = false;
	let fetchFailed = false;
	let loadFinished = false;

	$: actionCopy = {
		open: $format('schedule_action_open', { default: 'open' }),
		close: $format('schedule_action_close', { default: 'close' }),
		on: $format('schedule_action_on', { default: 'on' }),
		off: $format('schedule_action_off', { default: 'off' }),
		toggle: $format('schedule_action_toggle', { default: 'toggle' }),
		boost: $format('sub_settings_title_boost', { default: 'Boost' }),
		min: $format('action_delay_message_units_min', { default: 'min' }),
	};
	$: timeLabel = occurrence ? getTimeFormat(occurrence.hour, occurrence.min) : '';
	$: actionPhrases = actions.map((action) =>
		formatNextScheduleAction(action, actionCopy, (celsius) =>
			formatNextScheduleTarget(celsius, device.ht_ui?.temperature_unit),
		),
	);
	$: valueLabel = formatNextScheduleValue(timeLabel, actionPhrases);
	$: nextLabel = capFirstChar(
		$format('label_next_schedule', {
			default: 'Next schedule',
		}),
	);
	$: unavailableLabel = capFirstChar(
		$format('label_data_unavailable', {
			default: 'Data unavailable',
		}),
	);
	$: seeAllLabel = capFirstChar(
		$format('button_see_all_schedules', {
			default: 'See all schedules',
		}),
	);
	$: tryAgainLabel = capFirstChar(
		$format('button_try_again', {
			default: 'try again',
		}),
	);
	$: showError = loadFinished && fetchFailed && !occurrence;
	$: showCard = Boolean(occurrence) || showError;
	$: cardValue = showError ? unavailableLabel : valueLabel;
	$: cardIcon = showError ? 'fa-solid fa-clock' : nextScheduleActionIcon(actions);
	$: ctaLabel = showError ? tryAgainLabel : seeAllLabel;
	$: isDisabled = Boolean(disabled);
	$: canOpen = !isDisabled && !device.shared;
	$: showCta = showError || canOpen;
	$: ariaLabel = [nextLabel, cardValue].filter(Boolean).join('. ');

	const applySchedules = (schedules: DeviceSchedulesFetchResult['schedules']) => {
		occurrence = findNextSchedule(schedules);
		actions = occurrence ? parseNextScheduleActions(occurrence.schedule.calls) : [];
	};

	const loadSchedules = async (useCache = true) => {
		const seq = ++loadSeq;
		if (useCache) {
			const cached = getCachedDeviceSchedules(device);
			if (seq === loadSeq && cached.length) {
				fetchFailed = false;
				applySchedules(cached);
			}
		}
		const fetched = await fetchDeviceSchedules(device);
		if (seq !== loadSeq) return;
		if (fetched.ok || fetched.schedules.length) {
			fetchFailed = false;
			applySchedules(fetched.schedules);
		} else {
			fetchFailed = true;
			occurrence = undefined;
			actions = [];
		}
		loadFinished = true;
	};

	const openSchedules = () => {
		if (!canOpen) return;
		navigateToSetting(device.id, 'schedule', '', true);
	};

	const onCtaClick = (event: MouseEvent) => {
		event.stopPropagation();
		if (showError) {
			loadSchedules(false);
			return;
		}
		openSchedules();
	};

	const onCtaKeydown = (event: KeyboardEvent) => {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		event.stopPropagation();
		if (showError) {
			loadSchedules(false);
			return;
		}
		openSchedules();
	};

	onMount(() => {
		scheduleRev = getDeviceScheduleRev(device);
		watchingRev = true;
		loadSchedules();
	});

	onDestroy(() => {
		loadSeq += 1;
	});

	$: if (watchingRev && settings) {
		const nextRev = getDeviceScheduleRev(device);
		if (nextRev != null && nextRev !== scheduleRev) {
			scheduleRev = nextRev;
			loadSchedules(false);
		}
	}
</script>

{#if showCard}
	<div
		id="next-schedule-card"
		class="next-schedule-card span-full"
		data-span="full"
		data-property-id="nextSchedule"
		role="status"
		aria-label={ariaLabel}
	>
		<PropertyCard
			class="width-full"
			priority="wide"
			kind="status"
			tone={showError ? 'muted' : undefined}
			value={cardValue}
			label={nextLabel}
			icon={cardIcon}
			{stale}
			disabled={isDisabled}
			hideCaption
		>
			<svelte:fragment slot="bottomRight">
				{#if showCta}
					<Text
						element="div"
						styleType="tiny"
						class="theme-text-accent link-underline next-schedule-cta"
						id={showError ? 'next-schedule-try-again' : 'next-schedule-see-all'}
						role="button"
						tabindex="0"
						aria-label={ctaLabel}
						on:click={onCtaClick}
						on:keydown={onCtaKeydown}
					>
						{ctaLabel}
					</Text>
				{/if}
			</svelte:fragment>
		</PropertyCard>
	</div>
{/if}

<style>
	.next-schedule-card {
		min-width: 0;
	}

	.next-schedule-card :global(.next-schedule-cta) {
		cursor: pointer;
	}
</style>
